import { type TechlineStatusCode } from '@packages/domain/techline/cases';
import { AddComment, FetchComments, type TechlineCommentEntity } from '@packages/domain/techline/comments';
import { container } from '@web/services/container';
import { useCallback, useEffect, useState } from 'react';

import { broadcastCaseStatusChange } from '../../utils/case-tab-sync';

// Operations are prefixed with $ to make them visually distinct in this file.
const [$addTechlineComment, $fetchTechlineComments] = container.resolveOperations(AddComment, FetchComments);

/** Parameters for the useComments hook. */
export type UseCommentsParams = {
    /** The case number to manage comments for. */
    caseNumber: string | undefined;

    /** The current case status code, used to determine post-comment behavior. */
    caseStatusCode: TechlineStatusCode | undefined;

    /** Whether the hook should fetch data on mount. */
    isEnabled: boolean;

    /**
     * Callback fired after a comment is successfully posted. The parent hook
     * uses this to refresh case details (the backend may transition the case
     * status as a side effect of the comment).
     */
    onCommentPosted?: () => void;

    /** The retailer number associated with the case. */
    retailerNumber: string | undefined;

    /** The username of the current user. */
    username: string | undefined;
};

/**
 * Manage techline case comments: fetching and adding. All API interactions
 * are delegated to use cases; this hook owns only React state and lifecycle.
 * When isEnabled is false, the initial fetch is skipped entirely.
 *
 * @param params - the case context for comment operations
 * @returns commentData state and mutation functions
 */
export function useComments(params: UseCommentsParams) {
    const { caseNumber, caseStatusCode, isEnabled, onCommentPosted, username, retailerNumber } = params;

    const [commentData, setCommentData] = useState<TechlineCommentEntity[]>([]);
    const [isCommentLoading, setIsCommentLoading] = useState(false);

    /**
     * Fetch comments for the current case and update local state.
     *
     * @param caseNum - the case number to fetch comments for
     */
    const fetchComments = useCallback(async (caseNum: string) => {
        try {
            const result = await $fetchTechlineComments({ caseNumber: caseNum });
            setCommentData(result);
        } catch (err) {
            alert('Error: ' + err + '. Please refresh & try again.');
        }
    }, []);

    /** Fetch comments on mount when enabled and a case number is available. */
    useEffect(() => {
        if (isEnabled && caseNumber) {
            fetchComments(caseNumber);
        }
    }, [caseNumber, isEnabled, fetchComments]);

    /**
     * Add a comment to the current case. Posts the comment, refreshes the
     * comment list on success, and manages loading state.
     *
     * @param message - the comment text to post
     * @returns whether the comment was successfully posted
     */
    const addComment = useCallback(
        async (message: string): Promise<boolean> => {
            if (!caseNumber || !caseStatusCode || !username || !retailerNumber) {
                return false;
            }

            setIsCommentLoading(true);

            try {
                const success = await $addTechlineComment({
                    caseNumber,
                    caseStatusCode,
                    username,
                    retailerNumber,
                    message,
                });

                if (success) {
                    await fetchComments(caseNumber);
                    broadcastCaseStatusChange({ caseNumber, retailerNumber });
                    onCommentPosted?.();
                }

                setIsCommentLoading(false);
                return success;
            } catch (err) {
                alert('Error: ' + err + '. Please refresh & try again.');
                setIsCommentLoading(false);
                return false;
            }
        },
        [caseNumber, caseStatusCode, fetchComments, onCommentPosted, retailerNumber, username]
    );

    /**
     * Refresh the comment list for the current case.
     * No-op if no case number is available.
     */
    const refreshComments = useCallback(async () => {
        if (caseNumber) {
            await fetchComments(caseNumber);
        }
    }, [caseNumber, fetchComments]);

    return {
        /** Post a new comment to the current case. Returns whether the comment was successfully posted. */
        addComment,

        /** The current list of comments for the case. */
        commentData,

        /** Whether a comment is currently being posted. */
        isCommentLoading,

        /** Re-fetch comments from the API and update local state. */
        refreshComments,
    };
}
